import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, logDev } from "../api";
import { ShieldCheck, Printer, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import AddToLinkedInProfileButton from "../components/AddToLinkedInProfileButton";

export default function LMSCertificates() {
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const { user } = useAuth();
  const certRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get("/me/lms/certificates")
      .then(r => { if (alive) setList(r.data); })
      .catch(err => logDev.error("[lms-certs] load failed:", err));
    return () => { alive = false; };
  }, []);

  const printCert = () => {
    if (!certRef.current || !active) return;

    // SAFE: build a hidden iframe and copy DOM into it instead of using
    // the legacy `doc.write` print API (which is an XSS-prone approach). The cert content is
    // rendered by React so we trust the structure; we just clone the node.
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    Object.assign(iframe.style, {
      position: "fixed", right: "0", bottom: "0",
      width: "0", height: "0", border: "0", visibility: "hidden",
    });
    document.body.appendChild(iframe);

    const idoc = iframe.contentDocument;
    if (!idoc) {
      document.body.removeChild(iframe);
      return;
    }

    // <head>
    const labColor = active.lab_color || "#006CFF";
    const title = idoc.createElement("title");
    title.textContent = active.cert_no || "SkillConnect Certificate";
    idoc.head.appendChild(title);

    const style = idoc.createElement("style");
    // labColor is a hex from our seed data, but we still keep all
    // interpolations inside a static CSS string (no user-controllable text).
    style.textContent = `
      @page { size: A4 landscape; margin: 0; }
      body { margin: 0; font-family: 'DM Sans', system-ui, sans-serif; }
      .cert { width: 100%; min-height: 100vh; padding: 60px; box-sizing: border-box;
        background: linear-gradient(135deg, ${labColor}11 0%, white 50%);
        border: 12px solid ${labColor}; }
      .ribbon { width: 80px; height: 4px; background: ${labColor}; margin-bottom: 24px; }
      .brand { font-weight: 800; letter-spacing: -.02em; font-size: 22px; display:flex; align-items:center; gap:12px; }
      .signal { color: ${labColor}; }
      h1 { font-size: 56px; line-height: 1; margin: 24px 0 12px; font-weight: 800; }
      .name { font-size: 64px; font-weight: 800; margin: 24px 0; color: ${labColor}; }
      .lab { font-size: 28px; font-weight: 600; }
      .meta { margin-top: 40px; display: flex; justify-content: space-between; font-size: 14px; color: #555; }
      .cert-no { font-family: 'JetBrains Mono', monospace; font-size: 12px; }
    `;
    idoc.head.appendChild(style);

    // <body> — clone the rendered cert node (NEVER user-controlled HTML)
    const clone = certRef.current.cloneNode(true);
    idoc.body.appendChild(clone);

    iframe.contentWindow.focus();
    setTimeout(() => {
      try { iframe.contentWindow.print(); }
      catch (err) { logDev.error("[lms-certs] print failed:", err); }
      // Clean up shortly after the print dialog is dismissed.
      setTimeout(() => {
        try { document.body.removeChild(iframe); }
        catch (err) { logDev.warn("[lms-certs] iframe cleanup:", err); }
      }, 1500);
    }, 250);
  };

  return (
    <div className="space-y-6">
      {!active && (
        <>
          <div className="flex items-end justify-between">
            <div>
              <span className="eyebrow">Verifiable credentials</span>
              <h2 className="font-display font-bold text-3xl mt-1">My Certificates</h2>
            </div>
            <span className="text-sm text-muted">{list.length} earned</span>
          </div>
          {list.length === 0 ? (
            <div className="card card-pad text-muted" data-testid="no-certs">
              No certificates yet. Finish a CoE lab to earn one.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {list.map(c => (
                <button key={c.id} onClick={() => setActive(c)}
                  className="card card-pad text-left card-hover" data-testid={`cert-${c.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs px-2 py-1 rounded-sm"
                      style={{ background: `${c.lab_color}15`, color: c.lab_color, border: `1px solid ${c.lab_color}40` }}>
                      {c.lab_code}
                    </span>
                    <ShieldCheck size={18} style={{ color: c.lab_color }} />
                  </div>
                  <h3 className="font-display font-bold text-lg mt-3">{c.lab_name}</h3>
                  <div className="text-xs text-muted mt-2 font-mono">{c.cert_no}</div>
                  <div className="text-xs text-muted">Issued {new Date(c.issued_at).toLocaleDateString()}</div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {active && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <button onClick={() => setActive(null)} className="inline-flex items-center gap-2 text-sm font-semibold text-signal" data-testid="cert-back">
              <ArrowLeft size={14} /> Back to certificates
            </button>
            <div className="flex items-center gap-2">
              <AddToLinkedInProfileButton
                name={active.lab_name}
                organizationName="SkillConnect"
                issueYear={active.issued_at ? new Date(active.issued_at).getFullYear() : undefined}
                issueMonth={active.issued_at ? new Date(active.issued_at).getMonth() + 1 : undefined}
                certUrl={typeof window !== "undefined" ? `${window.location.origin}/verify/${active.cert_no}` : undefined}
                certId={active.cert_no}
              />
              <button onClick={printCert} className="btn-primary" data-testid="cert-print-btn">
                <Printer size={14} /> Print / Save PDF
              </button>
            </div>
          </div>

          <div ref={certRef} className="cert" data-testid="cert-viewer"
            style={{
              background: `linear-gradient(135deg, ${active.lab_color}11 0%, white 50%)`,
              border: `12px solid ${active.lab_color}`,
              padding: 60,
            }}>
            <div className="ribbon" style={{ width: 80, height: 4, background: active.lab_color, marginBottom: 24 }} />
            <div className="brand" style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img src="/brand/skillconnect-logo.png?v=8" alt="SkillConnect" style={{ height: 44, width: "auto", objectFit: "contain" }} />
            </div>
            <h1>Certificate of Completion</h1>
            <div className="font-mono uppercase tracking-widest text-sm text-muted">This is to certify that</div>
            <div className="name" style={{ color: active.lab_color }}>{active.student_name || user?.name}</div>
            <div className="font-mono uppercase tracking-widest text-sm text-muted">has successfully completed</div>
            <div className="lab">{active.lab_name}</div>
            <div className="meta">
              <div>
                <div className="font-mono uppercase text-xs text-muted">Lab code</div>
                <div className="font-semibold">{active.lab_code}</div>
              </div>
              <div>
                <div className="font-mono uppercase text-xs text-muted">Issued</div>
                <div className="font-semibold">{new Date(active.issued_at).toLocaleDateString()}</div>
              </div>
              <div>
                <div className="font-mono uppercase text-xs text-muted">Credential ID</div>
                <div className="cert-no font-mono text-xs">{active.cert_no}</div>
              </div>
            </div>
          </div>
        </>
      )}

      <Link to="/student/lms" className="link-underline text-sm text-signal">← Back to LMS</Link>
    </div>
  );
}
